import { env } from "@/env.mjs";
import { createId } from "@paralleldrive/cuid2";
import type { JSONValue } from "ai";
import { type ClassValue, clsx } from "clsx";
import type { Session } from "next-auth";
import type { PostHog } from "posthog-js";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get the URL of the current environment.
 * @param {Object} options - The options object.
 * @param {boolean} options.ngrok - Whether to use ngrok URL.
 * @returns {string} The URL of the current environment without a trailing `/`.
 * @example
 * const url = getURL({ ngrok: true });
 * // => 'https://example.ngrok.io'
 * @example
 * const url = getURL();
 * // => 'https://example.com'
 * @example
 * const url = getURL({ ngrok: false });
 * // => 'http://localhost:3000'
 */
export const getURL = ({ ngrok = false } = {}) => {
  let url =
    process?.env?.NEXT_PUBLIC_SITE_URL ?? // Site URL in production env.
    process?.env?.NEXT_PUBLIC_VERCEL_URL ?? // Automatically set by Vercel.
    "http://localhost:3000";

  if (ngrok) {
    if (url === "http://localhost:3000") {
      url = env.NEXT_PUBLIC_NGROK_URL;
    }
  }

  // Make sure to include `https://` when not localhost.
  url = url.includes("http") ? url : `https://${url}`;
  // Make sure to remove trailing `/`.
  url = url.charAt(url.length - 1) === "/" ? url.slice(0, -1) : url;
  return url;
};

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const trackEvent = ({
  posthog,
  eventName,
  properties,
  session,
}: {
  posthog: PostHog;
  eventName: string;
  properties: { [key: string]: unknown };
  session: Session | null;
}) => {
  posthog.capture(eventName, {
    ...properties,
    ...(session?.user.id && { distinctId: session.user.id }),
  });
};

export const findValueByKey = (
  jsonArray: JSONValue[],
  fullKey: string,
): string | undefined => {
  // Iterate over the array of JSON objects
  for (const item of jsonArray) {
    // Ensure the item is an object and not null
    if (typeof item === "object" && item !== null && !Array.isArray(item)) {
      // Check if the item has the key we're looking for
      if (Object.prototype.hasOwnProperty.call(item, fullKey)) {
        // Return the value associated with the fullKey
        return item[fullKey] as string;
      }
    }
  }
  // If the fullKey is not found in any of the objects, return undefined
  return undefined;
};

export const safeParseJSON = <T>(
  data: string | undefined,
  defaultValue: T,
): T => {
  try {
    if (typeof data !== "string") {
      return defaultValue;
    }
    return JSON.parse(data ?? "") as T;
  } catch (error) {
    console.warn("Could not parse JSON, returning default value", error);
    return defaultValue;
  }
};

export const cuid = createId;

export const generateRandomHex = (): string => {
  const hexChars = "0123456789ABCDEF";
  let hex = "#";

  for (let i = 0; i < 6; i++) {
    hex += hexChars[Math.floor(Math.random() * 16)];
  }

  return hex;
};

/**
 * Plurazes a word
 * @description pluralize a word based on the count
 * @param word The word to pluralize
 * @param count The count of the word
 */
export function pluralize(word: string, count: number): string {
  // If word is ending with y and count is greater than 1
  if (word.endsWith("y") && count > 1) {
    return `${word.slice(0, -1)}ies`;
  }
  // If word is ending with s
  if (word.endsWith("s")) {
    return word;
  }
  // If count is greater than 1
  if (count > 1) {
    return `${word}s`;
  }
  return word;
}

export function isExternalAttendee({
  attendeeEmail,
  userEmail,
}: {
  userEmail: string;
  attendeeEmail: string;
}) {
  const userDomain = userEmail.split("@")[1]?.toLowerCase();

  const attendeeDomain = attendeeEmail.split("@")[1];

  return attendeeEmail !== userEmail && attendeeDomain !== userDomain;
}

export function capitalizeAllWords(
  str: string | undefined | null | number,
): string {
  if (typeof str === "number") {
    return str.toString();
  }

  if (!str) {
    return "";
  }
  // Split the string into words based on spaces
  const words = str.split(" ");

  // Map over each word to capitalize the first letter
  const capitalizedWords = words.map((word) => {
    // Check if the word is not empty to avoid errors
    if (word[0]) {
      // Capitalize the first letter and concatenate the rest of the word
      return word[0].toUpperCase() + word.slice(1);
    }
    // Return the empty string (handles multiple spaces)
    return word;
  });

  // Join the capitalized words back into a single string
  return capitalizedWords.join(" ");
}
